import { MediaType } from "@/generated/graphql";
import { hasTranscription } from "../config";
import { MediaPreview } from "../previews";
import type { MediaItem } from "../types";
import { ReplaceFilePanel } from "./ReplaceFilePanel";

interface EditPreviewPanelProps {
  /** Media item being edited */
  media: MediaItem;
  /** Whether to show replace file section */
  showReplace: boolean;
  /** Current audio text (for transcription display) */
  audioText: string;
  /** Whether transcription is in progress */
  isTranscribing?: boolean;
  /** Transcription error message */
  transcriptionError?: string | null;
  /** Callback to trigger transcription */
  onGenerateTranscription?: () => void;
}

/**
 * EditPreviewPanel - Right panel for edit modal
 *
 * Shows media preview or replace file section based on state.
 * For audio/video, also shows transcription section.
 */
export function EditPreviewPanel({
  media,
  showReplace,
  audioText,
  isTranscribing = false,
  transcriptionError = null,
  onGenerateTranscription,
}: EditPreviewPanelProps) {
  const showTranscription = hasTranscription(media.type);

  if (showReplace) {
    return <ReplaceFilePanel mediaType={media.type} currentFilename={media.filename} />;
  }

  return (
    <div className="space-y-6">
      {/* Preview Section */}
      <section>
        <h3 className="mb-2 text-lg font-semibold text-gray-900">Preview</h3>
        <MediaPreview url={media.url} filename={media.filename} type={media.type} variant="full" />
      </section>

      {/* Transcription Section (for audio/video only) */}
      {showTranscription && (
        <section>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            {media.type === MediaType.Audio ? "Audio" : "Video"} to text
          </h3>
          <p className="mb-3 text-sm text-gray-600">
            Automatically transcribe {media.type === MediaType.Audio ? "audio" : "video"} using
            Azure Speech API. Supports MP3, M4A, WAV, and more.
          </p>

          {/* Transcription textarea */}
          <textarea
            value={audioText}
            readOnly
            rows={6}
            className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm"
            placeholder={
              isTranscribing
                ? "Transcribing... This may take a moment."
                : "Transcription will appear here..."
            }
          />

          {/* Error message */}
          {transcriptionError && (
            <div className="mt-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
              <strong>Error:</strong> {transcriptionError}
            </div>
          )}

          {/* Generate button */}
          <button
            type="button"
            onClick={onGenerateTranscription}
            disabled={isTranscribing || !onGenerateTranscription}
            className="mt-3 w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200"
          >
            {isTranscribing ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="h-4 w-4 animate-spin"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Transcribing...
              </span>
            ) : (
              "Generate transcription"
            )}
          </button>

          {/* Cost warning */}
          <p className="mt-2 text-xs text-gray-500">
            ℹ️ Transcription uses Azure Speech API (~$1 per hour of audio)
          </p>
        </section>
      )}
    </div>
  );
}

export default EditPreviewPanel;
